import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@ui/button';
import {
  DescriptionListLabel,
  DescriptionListValue,
  ButtonLink,
  Input,
} from '@web/atoms';
import { ApiResult } from '../../api';
import { useToast } from '../../Toast';
import {
  BoldText,
  PromoCodeContainer,
  OfferCodeContainer,
  OfferCodeLabel,
} from './styles.js';
import { getPlanFreeItems } from './functions';

export const PricingListDisplayFreeItems = ({
  plans,
  puptainerLabel,
  scooperLabel,
  bowlLabel,
  freeItems,
}) => {
  const planFreeItems = getPlanFreeItems(plans);

  return (
    <>
      {freeItems?.map(option => (
        <>
          <DescriptionListLabel>
            {`${option.label + ' '}`}
            {plans.length > 1 ? `X${plans.length}` : null}
          </DescriptionListLabel>

          <DescriptionListValue>
            <BoldText color="status.successPrimary">FREE</BoldText>
          </DescriptionListValue>
        </>
      ))}
      {planFreeItems.puptainerCount === 0 ? null : (
        <>
          <DescriptionListLabel>
            {puptainerLabel}{' '}
            {planFreeItems.puptainerCount > 1
              ? `X${planFreeItems.puptainerCount}`
              : null}
          </DescriptionListLabel>

          <DescriptionListValue>
            <BoldText color="status.successPrimary">FREE</BoldText>
          </DescriptionListValue>
        </>
      )}
      {planFreeItems.scooperCount === 0 ? null : (
        <>
          <DescriptionListLabel>
            {scooperLabel}{' '}
            {planFreeItems.scooperCount > 1
              ? `X${planFreeItems.scooperCount}`
              : null}
          </DescriptionListLabel>

          <DescriptionListValue>
            <BoldText color="status.successPrimary">FREE</BoldText>
          </DescriptionListValue>
        </>
      )}
      {planFreeItems.bowlCount === 0 ? null : (
        <>
          <DescriptionListLabel>
            {bowlLabel}{' '}
            {planFreeItems.bowlCount > 1 ? `X${planFreeItems.bowlCount}` : null}
          </DescriptionListLabel>

          <DescriptionListValue>
            <BoldText color="status.successPrimary">FREE</BoldText>
          </DescriptionListValue>
        </>
      )}
    </>
  );
};

PricingListDisplayFreeItems.displayName = 'PricingListDisplayFreeItems';
PricingListDisplayFreeItems.propTypes = {
  plans: PropTypes.arrayOf(
    PropTypes.shape({
      breeds: PropTypes.arrayOf(PropTypes.string).isRequired,
      petId: PropTypes.string.isRequired,
      price: PropTypes.string.isRequired,
      priceWithDiscount: PropTypes.string.isRequired,
      recipes: PropTypes.object.isRequired,
      numPacks: PropTypes.number.isRequired,
      bagCount: PropTypes.number.isRequired,
      productType: PropTypes.string,
    }),
  ),
  puptainerLabel: PropTypes.string.isRequired,
  scooperLabel: PropTypes.string.isRequired,
  bowlLabel: PropTypes.string,
  freeItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
    }),
  ),
};

export const PromoCodeInfo = ({
  promoCodeLabel,
  changePromoCodeLabel,
  applyPromoCodeLabel,
  removePromoCodeLabel,
  errorPromoCodeLabel,
  promoCodeAppliedLabel,
  newPromoCodeLabel,
  applyPromoCode,
  removePromoCode,
  isDefaultPromoCode = true,
}) => {
  const { showToast } = useToast();
  const [newPromoCode, setNewPromoCode] = useState('');
  const [editPromoCode, setEditPromoCode] = useState(false);
  const [isSubmittingPromoCode, setIsSubmittingPromoCode] = useState(false);

  const applyPromoCodeHandler = async e => {
    e.preventDefault();
    setIsSubmittingPromoCode(true);
    const newPromoCodeResult = await applyPromoCode({ newPromoCode });

    ApiResult.match(newPromoCodeResult, {
      success: () => {
        setEditPromoCode(false);
        setIsSubmittingPromoCode(false);
      },
      error: {
        server: () => {
          showToast({
            status: 'error',
            message: errorPromoCodeLabel,
            headline: 'Error Message',
          });
          setTimeout(() => setIsSubmittingPromoCode(false));
        },
        client: () => {
          showToast({
            status: 'error',
            message: errorPromoCodeLabel,
            headline: 'Error Message',
          });
          setTimeout(() => setIsSubmittingPromoCode(false));
        },
      },
    });
  };

  const removePromoCodeHandler = async e => {
    e.preventDefault();
    setIsSubmittingPromoCode(true);

    const removePromoCodeResult = await removePromoCode();

    ApiResult.match(removePromoCodeResult, {
      success: () => {
        setIsSubmittingPromoCode(false);
      },
      error: {
        server: () => {
          showToast({
            status: 'error',
            message: errorPromoCodeLabel,
            headline: 'Error Message',
          });
          setTimeout(() => setIsSubmittingPromoCode(false));
        },
        client: () => {
          showToast({
            status: 'error',
            message: errorPromoCodeLabel,
            headline: 'Error Message',
          });
          setTimeout(() => setIsSubmittingPromoCode(false));
        },
      },
    });
  };

  const onNewPromoCodeChange = promoCode => {
    setNewPromoCode(promoCode);
  };
  return (
    <>
      <PromoCodeContainer>
        <OfferCodeContainer>
          {!editPromoCode && (
            <>
              {isDefaultPromoCode && (
                <>
                  <OfferCodeLabel>{promoCodeLabel}</OfferCodeLabel>
                  <ButtonLink
                    onClick={() => setEditPromoCode(true)}
                    underline={true}
                    disabled={isSubmittingPromoCode}
                  >
                    {changePromoCodeLabel}
                  </ButtonLink>
                </>
              )}
              {!isDefaultPromoCode && (
                <>
                  <OfferCodeLabel>{promoCodeAppliedLabel}</OfferCodeLabel>
                  <ButtonLink
                    onClick={removePromoCodeHandler}
                    underline={true}
                    disabled={isSubmittingPromoCode}
                  >
                    {removePromoCodeLabel}
                  </ButtonLink>
                </>
              )}
            </>
          )}
          {editPromoCode && (
            <>
              <Input
                name="new-promo-code"
                value={newPromoCode}
                labelText={newPromoCodeLabel}
                visuallyHideLabel={true}
                onChange={onNewPromoCodeChange}
              />
              <Button
                fullWidth
                variant="primary"
                onClick={applyPromoCodeHandler}
                verticalPadding={3}
                disabled={isSubmittingPromoCode}
              >
                {applyPromoCodeLabel}
              </Button>
            </>
          )}
        </OfferCodeContainer>
      </PromoCodeContainer>
    </>
  );
};
PromoCodeInfo.displayName = 'PromoCodeInfo';
PromoCodeInfo.propTypes = {
  promoCodeLabel: PropTypes.string.isRequired,
  changePromoCodeLabel: PropTypes.string.isRequired,
  applyPromoCodeLabel: PropTypes.string.isRequired,
  removePromoCodeLabel: PropTypes.string.isRequired,
  errorPromoCodeLabel: PropTypes.string,
  promoCodeAppliedLabel: PropTypes.string,
  newPromoCodeLabel: PropTypes.string,
  applyPromoCode: PropTypes.func.isRequired,
  removePromoCode: PropTypes.func.isRequired,
  isDefaultPromoCode: PropTypes.bool.isRequired,
};
